/**
 * The 2001 wind chill temperature index adopted by the US National Weather Service and
 * the Meteorological Service of Canada (Joint Action Group for Temperature Indices):
 *
 *   WCT (°F) = 35.74 + 0.6215 T − 35.75 V^0.16 + 0.4275 T V^0.16     T in °F, V in mph
 *   WCT (°C) = 13.12 + 0.6215 T − 11.37 V^0.16 + 0.3965 T V^0.16     T in °C, V in km/h
 *
 * The two forms are the same equation in different units. Wind speed V is the speed at
 * the standard 10 m (33 ft) anemometer height; the model converts it to 5 ft, the height
 * of an adult face. The NWS defines the index only for air temperatures at or below 50 °F
 * and wind speeds above 3 mph; in lighter wind the "calm-air" value is the temperature.
 *
 * Frostbite times and risk levels are those published by Environment and Climate Change
 * Canada (canada.ca, "Wind chill and the Wind Chill Index"), by wind chill in °C.
 */

import {
  fromCelsius,
  type TemperatureUnit,
  toCelsius,
} from "@/lib/tool-kit/earth-science/temperature";

export type WindUnit = "mph" | "kmh" | "ms" | "kn";

const KMH_PER_UNIT: Record<WindUnit, number> = {
  mph: 1.609_344,
  kmh: 1,
  ms: 3.6,
  kn: 1.852,
};

export function windToMph(value: number, unit: WindUnit): number {
  return (value * KMH_PER_UNIT[unit]) / KMH_PER_UNIT.mph;
}

export function windFromMph(mph: number, unit: WindUnit): number {
  return (mph * KMH_PER_UNIT.mph) / KMH_PER_UNIT[unit];
}

/** Highest air temperature (°F) and lowest wind (mph, exclusive) the index is defined for. */
export const WIND_CHILL_MAX_TEMPERATURE_F = 50;
export const WIND_CHILL_CALM_MPH = 3;

/** The imperial formula, with no range checks. */
export function windChillFahrenheit(tF: number, vMph: number): number {
  const v = vMph ** 0.16;
  return 35.74 + 0.6215 * tF - 35.75 * v + 0.4275 * tF * v;
}

/** The metric form, with no range checks. */
export function windChillCelsius(tC: number, vKmh: number): number {
  const v = vKmh ** 0.16;
  return 13.12 + 0.6215 * tC - 11.37 * v + 0.3965 * tC * v;
}

export type WindChillStatus = "ok" | "calm" | "tooWarm";

export interface WindChillResult {
  status: WindChillStatus;
  airFahrenheit: number;
  windMph: number;
  /** The wind chill in °F; equals the air temperature when the status is not "ok". */
  fahrenheit: number;
  celsius: number;
  /** How many degrees colder than the air it feels (≥ 0). */
  drop: number;
}

export function windChill(
  temperature: number,
  temperatureUnit: TemperatureUnit,
  wind: number,
  windUnit: WindUnit
): WindChillResult {
  const airFahrenheit = fromCelsius(
    toCelsius(temperature, temperatureUnit),
    "F"
  );
  const windMph = windToMph(wind, windUnit);
  let status: WindChillStatus = "ok";
  if (airFahrenheit > WIND_CHILL_MAX_TEMPERATURE_F) {
    status = "tooWarm";
  } else if (windMph <= WIND_CHILL_CALM_MPH) {
    status = "calm";
  }
  const fahrenheit =
    status === "ok"
      ? windChillFahrenheit(airFahrenheit, windMph)
      : airFahrenheit;
  return {
    status,
    airFahrenheit,
    windMph,
    fahrenheit,
    celsius: toCelsius(fahrenheit, "F"),
    drop: airFahrenheit - fahrenheit,
  };
}

export type FrostbiteBand =
  | "none"
  | "low"
  | "moderate"
  | "high"
  | "veryHigh"
  | "severe"
  | "extreme";

/** Risk level by wind chill in °C, as published by Environment and Climate Change Canada. */
export function frostbiteBand(windChillC: number): FrostbiteBand {
  const value = Math.round(windChillC);
  if (value > 0) {
    return "none";
  }
  if (value >= -9) {
    return "low";
  }
  if (value >= -27) {
    return "moderate";
  }
  if (value >= -39) {
    return "high";
  }
  if (value >= -47) {
    return "veryHigh";
  }
  return value >= -54 ? "severe" : "extreme";
}
